import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../i18n';

const StorySection = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-28 md:py-40 bg-regency relative overflow-hidden"
      id="storia"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 damask-pattern opacity-35" />
      <div className="absolute top-0 left-0 w-72 h-72 bg-lavender/8 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-72 h-72 bg-blush/40 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <p className="font-display text-sm uppercase tracking-[0.5em] text-gold mb-5">
            {t('story.eyebrow')}
          </p>
          <h2 className="font-script text-5xl md:text-6xl lg:text-7xl text-lavender mb-8">
            {t('story.title')}
          </h2>
          <p className="font-elegant text-xl md:text-2xl text-muted-foreground italic max-w-xl mx-auto">
            {t('story.subtitle')}
          </p>
          <div className="flex items-center justify-center gap-5 mt-10">
            <span className="w-20 h-px bg-gradient-to-r from-transparent to-gold/50" />
            <span className="text-lavender/50">❦</span>
            <span className="w-20 h-px bg-gradient-to-l from-transparent to-gold/50" />
          </div>
        </div>

        {/* Video frame */}
        <div className={`max-w-md mx-auto transition-all duration-1000 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
        }`}>
          <div className="relative">
            {/* Outer decorative frame */}
            <div className="absolute -inset-3 rounded-2xl border border-gold/40 pointer-events-none" />
            <div className="absolute -inset-1.5 rounded-xl border border-lavender/25 pointer-events-none" />
            <div className="rounded-xl overflow-hidden shadow-elegant" data-testid="story-video">
              <video
                src="/storia.webm"
                autoPlay
                loop
                muted
                playsInline
                controls
                className="w-full h-auto"
              />
            </div>
          </div>
          <p className="text-center font-elegant text-lg text-muted-foreground italic mt-8">
            {t('story.caption')}
          </p>
        </div>

        {/* Date card */}
        <div className={`mt-20 transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="bg-card/90 backdrop-blur-sm rounded-xl p-8 md:p-10 border border-gold/25 shadow-elegant max-w-md mx-auto relative gold-frame text-center">
            <p className="font-display text-xs uppercase tracking-[0.5em] text-gold mb-5">
              {t('saveDate.label')}
            </p>
            <p className="font-display text-3xl md:text-4xl text-foreground mb-4 tracking-wide">
              {t('date.full')}
            </p>
            <div className="flex items-center justify-center gap-4 my-5">
              <span className="w-10 h-px bg-gold/40" />
              <span className="text-lavender/60">❦</span>
              <span className="w-10 h-px bg-gold/40" />
            </div>
            <p className="font-elegant text-2xl text-muted-foreground italic">
              Villa Gazzani
            </p>
            <p className="font-body text-sm text-muted-foreground mt-2 tracking-wider">
              Civitanova Marche
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StorySection;
